import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterSectionTitleSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';

const ArticleListPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [articleForm, setArticleForm] = useState({ _id: '', title: '', percentage: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${baseURL}/articles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setArticles(response.data);
      } catch (err) {
        setError('Failed to fetch articles');
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setArticleForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateArticle = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);
    try {
      const token = localStorage.getItem('token');
      let response;
      if (isEditing) {
        response = await axios.put(`${baseURL}/articles/update/${articleForm._id}`, articleForm, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        setArticles((prev) =>
          prev.map((article) => (article._id === articleForm._id ? response.data.article : article))
        );
      } else {
        response = await axios.post(`${baseURL}/articles/create`, articleForm, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        setArticles((prev) => [response.data.article, ...prev]);
      }
      setArticleForm({ _id: '', title: '', percentage: '' });
      setIsEditing(false);
      setCreateSuccess(true);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create/update article');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditArticle = (article) => {
    setArticleForm({ _id: article._id, title: article.title, percentage: article.percentage });
    setIsEditing(true);
  };

  const handleDeleteArticle = async () => {
    if (!articleToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/articles/delete/${articleToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArticles((prev) => prev.filter((article) => article._id !== articleToDelete._id));
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
      setCreateSuccess(true);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to delete article');
    }
  };

  const handleCloseSnackbar = () => {
    setCreateSuccess(false);
    setCreateError(null);
  };

  return (
    <MasterPageShell title="Articles" subtitle="Master · Metals & rates basis" loading={loading}>
      {error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography sx={masterSectionTitleSx}>
                {isEditing ? 'Edit article' : 'Create article'}
              </Typography>
              <Box component="form" onSubmit={handleCreateArticle} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="title"
                  label="Article title"
                  name="title"
                  value={articleForm.title}
                  onChange={handleInputChange}
                  sx={masterFieldSx}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="percentage"
                  label="Percentage"
                  name="percentage"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={articleForm.percentage}
                  onChange={handleInputChange}
                  sx={masterFieldSx}
                />
                <Button type="submit" fullWidth variant="contained" sx={masterPrimaryBtnSx} disabled={createLoading}>
                  {createLoading
                    ? isEditing
                      ? 'Updating…'
                      : 'Creating…'
                    : isEditing
                      ? 'Update article'
                      : 'Create article'}
                </Button>
                {isEditing ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setIsEditing(false);
                      setArticleForm({ _id: '', title: '', percentage: '' });
                    }}
                    sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 650 }}
                  >
                    Cancel
                  </Button>
                ) : null}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={masterListPaperSx}>
              <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ ...masterSectionTitleSx, mb: 0 }}>Articles list</Typography>
                <Chip size="small" label={`${articles.length} items`} sx={{ fontWeight: 700, bgcolor: '#f1f5f9' }} />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={masterTableHeadCellSx}>Title</TableCell>
                      <TableCell align="right" sx={masterTableHeadCellSx}>Percentage</TableCell>
                      <TableCell align="right" sx={masterTableHeadCellSx}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {articles.map((article) => (
                      <TableRow key={article._id} hover>
                        <TableCell sx={{ fontWeight: 650, py: 1.15 }}>{article.title}</TableCell>
                        <TableCell align="right">{article.percentage}%</TableCell>
                        <TableCell align="right">
                          <IconButton color="primary" size="small" onClick={() => handleEditArticle(article)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => {
                              setArticleToDelete(article);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {articles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: '#94a3b8' }}>
                          No articles yet
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 750 }}>Delete article</DialogTitle>
        <DialogContent>
          <Typography>Delete “{articleToDelete?.title}”? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteArticle} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={createSuccess} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%', borderRadius: 2 }}>
          {isEditing ? 'Article updated successfully!' : 'Article saved successfully!'}
        </Alert>
      </Snackbar>
      <Snackbar open={!!createError} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          {createError}
        </Alert>
      </Snackbar>
    </MasterPageShell>
  );
};

export default ArticleListPage;
